import { useEffect, useState, ChangeEvent } from 'react';
import { TextareaAutosize, Dialog, DialogContent, DialogContentText, DialogActions, Select, InputBase, FormControl, MenuItem, AppBar, Container, Toolbar, Typography, Box, Grid, Button, Paper } from '@mui/material';
import * as IdbKeyVal from "idb-keyval";
import { useNavigate } from "react-router-dom";
import * as GolferHelper from '../library/GolferHelper';
import * as GrindLookup from '../library/GrindLookup';
import * as GrindTables from '../library/GrindTables';
import Golfer, { EnhancedFittingData } from '../classes/Golfer';
import Fitter from '../classes/Fitter';
import * as FitterHelper from '../library/FitterHelper';
import * as FittingBL from './FittingBL';
import { ShotHistory } from '../classes/ShotSelection';
import grind_d_sm11 from '../images/SM11-Grinds-D.jpg';
import grind_f_sm11 from '../images/SM11-Grinds-F.jpg';
import grind_k_sm11 from '../images/SM11-Grinds-K.jpg';
import grind_m_sm11 from '../images/SM11-Grinds-M.jpg';
import grind_s_sm11 from '../images/SM11-Grinds-S.jpg';
import grind_t_sm11 from '../images/SM11-Grinds-T.jpg';
import * as Globals from '../library/Globals';
import { styled } from '@mui/material/styles';
import { Person } from '@mui/icons-material';
import GolferMenu from './GolferMenu';
import * as WedgeOrderHelper from '../library/WedgeOrderHelper';
import * as TrackManHelper from '../library/TrackManHelper';
import { HtmlTooltip } from './HtmlTooltip';
import * as TutorialHelper from '../library/TutorialHelper';
import ISimulator from '../classes/ISimulator';
import * as SimulatorHelper from '../library/SimulatorHelper';
import SimulatorData from '../classes/SimulatorData';
import { FormattedMessage, useIntl } from 'react-intl';
import TagManager from 'react-gtm-module';
import { WedgeOrder } from '../classes/WedgeOrder';

// LOTS of good comments and more options for scrollable grid columns:
// https://stackoverflow.com/questions/52409322/material-ui-grid-with-independent-scrolling-columns

export default function WhyGrinds() {
    const queryParams = new URLSearchParams(window.location.search)
    const fromFittingHistory = (queryParams.get("fitting") ? true : false);  //query string flag if this is a redirect from the fitting history page
    const fromInQueue = (queryParams.get("queued") ? true : false); //query string for queued=1

    const [golfer, setGolfer] = useState(new Golfer());
    const [fitter, setFitter] = useState(new Fitter());
    const [grindRecommendations, setGrindRecommendations] = useState([] as GrindLookup.LoftGrindRecommendation[]);  //lets user tweak which grinds they want to buy
    const [yourTechnique, setYourTechnique] = useState(new WedgeOrderHelper.YourTechnique("--", "--", "--"));
    const [showTutorial, setShowTutorial] = useState(false);
    const [tutorialId, setTutorialId] = useState(0);
    const [showFinishedPopup, setShowFinishedPopup] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [simulator, setSimulator] = useState({} as ISimulator);
    const [orderConfirmationMessage, setOrderConfirmationMessage] = useState("");
    const [fitterIsAuthorized, setFitterIsAuthorized] = useState(true);  //when saving fittings, if fitter authorization expires and refresh token doesn't work, force fitter to login again after putting the fitting in the queue as unsent
    const [lblSubmit, setLblSubmit] = useState("");

    const intl = useIntl();
    const intlSoft = intl.formatMessage({ id: "golfer.info.soft" });
    const intlSlightlySoft = intl.formatMessage({ id: "golfer.info.slightly.soft" });
    const intlMedium = intl.formatMessage({ id: "golfer.info.medium" });
    const intlSlightlyFirm = intl.formatMessage({ id: "golfer.info.slightly.firm" });
    const intlFirm = intl.formatMessage({ id: "golfer.info.firm" });
    const intlLow = intl.formatMessage({ id: "golfer.info.low" });
    const intlLess = intl.formatMessage({ id: "golfer.info.less" });
    const intlMore = intl.formatMessage({ id: "golfer.info.more" });
    const intlHigh = intl.formatMessage({ id: "golfer.info.high" });
    const intlSendingToServer = intl.formatMessage({ id: "notes.sending.to.server" });
    const intlFinishedFitting = intl.formatMessage({ id: "now.hitting.finished.fitting" });
    const intlSaveAndSend = intl.formatMessage({ id: "general.save.and.send" });
    const intlResendSpecs = intl.formatMessage({ id: "notes.resend.specs" });
    const intlTitle = intl.formatMessage({ id: "now.hitting.why" });

    const usingSm11 = GrindTables.showSM11(fitter.showSm11);

    //10/2023: Adding logging to better track and TypeError being triggered somewhere in this block of order properties
    const OrderErrorKey = "wedgeOrderErrorKey";

    useEffect((): void => {
        if (fromFittingHistory) {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'vwf_why_these_grinds_history',
                    fit_event_status: 'complete - why grinds - review',
                    fit_id: golfer.orderId
                }
            });
        } else {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'vwf_why_these_grinds',
                }
            });
        }
        // resetTimeout();
        const getAsyncData = async () => {
            //get simulator object
            let newSimulator = await SimulatorHelper.getSimulator();
            setSimulator(newSimulator);

            //tutorial flag
            setShowTutorial(await TutorialHelper.isActive());

            let newFitter = await FitterHelper.getFitter();
            setFitter(newFitter);

            let newGolfer = await GolferHelper.getCurrentGolfer();
            setLblSubmit(newGolfer.email !== "" ? intlSaveAndSend : intlFinishedFitting);

            // FittingBL.calculateNextActiveShot(activeShotCoordinate, newGolfer);

            //populate the Your Technique sidebar
            var newYourTechnique = WedgeOrderHelper.calculateYourTechnique(newGolfer, newFitter, newSimulator, { ...yourTechnique });
            setYourTechnique(newYourTechnique);

            //if this is from the fitter history page, then don't change the grind recommendations on entry.
            //if not from fitter history page, then refresh grind recommendations based on latest settings
            if (!(fromFittingHistory || fromInQueue)) {
                //get the list of recommended lofts and all grind combinations
                let grinds = GrindLookup.getGrindsWithBunkerConfidence(newGolfer, newFitter, newSimulator);
                let newGrindRecommendations = GrindLookup.formatGrindsForOutput(grinds, newGolfer, newSimulator, intl);
                setGrindRecommendations(newGrindRecommendations);
                //save golfer object to local storage so we don't lose their grind preferences
                newGolfer.grindRecommendations = newGrindRecommendations;
                await GolferHelper.upsertGolfer(newGolfer);
            } else {
                setGrindRecommendations(newGolfer.grindRecommendations);
            }
            setGolfer(newGolfer);
        }

        getAsyncData();
    }, []);

    let navigate = useNavigate();

    const handleSubmit = async () => {
        let dataLayer = WedgeOrderHelper.getGtmDataLayer('vwf_why_these_grinds_email_specs', golfer, fitter, yourTechnique, false);
        TagManager.dataLayer({
            dataLayer: dataLayer
        });
        setShowSpinner(true);
        setShowFinishedPopup(true);
        let tmConfiguration = await TrackManHelper.getConfiguration();

        let newGolfer = { ...golfer };
        let wedgeOrder = new WedgeOrder();
        try {
            wedgeOrder = await WedgeOrderHelper.createOrder(golfer, fitter, tmConfiguration);
        }
        catch (err) {
            newGolfer.orderStatus = 'error';
            await GolferHelper.upsertGolfer(newGolfer);
            let orderError = await IdbKeyVal.get(OrderErrorKey);
            TagManager.dataLayer({
                dataLayer: {
                    event: 'log_error',
                    description: 'Why Grinds - createOrder()',
                    msg: 'catch: ' + orderError + " | " + err
                }
            });
            navigate("/apps/wedgefitting/golferinfo");
        }
        //new error check b/c some users are just clicking through the app without hitting any practice shots.
        var numShots = 0;
        golfer.loftsForFitting.forEach(loft => numShots += loft.practiceShots.filter(shot => shot.status === "closed").length);
        if (wedgeOrder.objectId !== "" && numShots > 0) {
            // newGolfer.orderId = wedgeOrder.objectId;  - now putting the orderId in golfer object creation
            //keep the message queued up till successfully sent
            newGolfer.orderStatus = 'queued';
            await setGolfer(newGolfer);
            try {
                let status = await WedgeOrderHelper.placeOrder(fitter.awsIdToken, wedgeOrder);
                if (status === 200) {
                    Globals.logDev(`${new Date()}, 'ORDER SENT'`);
                    let dataLayer = WedgeOrderHelper.getGtmDataLayer('vwf_fitting_complete_email_sent', golfer, fitter, yourTechnique, false);
                    TagManager.dataLayer({
                        dataLayer: dataLayer
                    });

                    newGolfer.orderStatus = 'sent';
                    //remove the enhanced fitting data b/c it takes up too much memory and we don't need it in the browser after it has been saved to the back-end
                    newGolfer.enhancedFittingData = [] as EnhancedFittingData[];
                    //                    await GolferHelper.upsertGolfer(newGolfer);
                }
            }
            catch (err) {
                TagManager.dataLayer({
                    dataLayer: {
                        event: 'log_error',
                        description: 'Why Grinds - Email specs',
                        msg: 'catch: ' + err
                    }
                });
                // if fitter unauthorized (most likely b/c refresh token has expired) then redirect to fitter login page when they close this page
                // debating whether to use this code or not. I think it's safer to say that if anything bad happens on the backend then force a new login. Better safe than sorry for now
                if (err.message.toLowerCase().indexOf('refresh') > -1 || err.message.indexOf('401') > -1) {
                    setFitterIsAuthorized(false);
                }

                //something went wrong on backend call. If not connected to the network, it will re-sync the queue when reconnected.
                //If not a network error, then probably an authentication error so force fitter to log back in to refresh all their authentication info.
                if (!Globals.errMsgIsOffline(err.message)) {
                    setFitterIsAuthorized(false);
                }
            }
            setOrderConfirmationMessage(intl.formatMessage({ id: "notes.complete" }) + "!");

            await GolferHelper.upsertGolfer(newGolfer);
            setShowSpinner(false);
            setShowFinishedPopup(true);
        } else {
            //no practice shots made. Redirect to home page to restart the from scratch
            TagManager.dataLayer({
                dataLayer: {
                    event: 'log_info',
                    description: 'Why Grinds - No practice shots taken',
                    msg: 'User submitted order without taking any practice shots'
                }
            });
            navigate("/apps/wedgefitting");
        }
    }

    const handleBack = () => {
        navigate('/apps/wedgefitting/fitting');
    }

    const handleSelectedChange = async (event: ChangeEvent<HTMLInputElement>) => {
        var newGolfer = { ...golfer, [event.target.name]: event.target.value };

        //recalculate the recommended grinds
        // let grinds = GrindLookup.getGrinds(newGolfer, simulator);
        let grinds = GrindLookup.getGrindsWithBunkerConfidence(newGolfer, fitter, simulator);

        let newGrindRecommendations = GrindLookup.formatGrindsForOutput(grinds, newGolfer, simulator, intl);
        setGrindRecommendations(newGrindRecommendations);
        newGolfer = { ...newGolfer, grindRecommendations: newGrindRecommendations };
        setGolfer(newGolfer);
        if (!(fromFittingHistory || fromInQueue)) {
            await GolferHelper.upsertGolfer(newGolfer);
        }
    }

    const BootstrapInput = styled(InputBase)(({ theme }) => ({
        'label + &': {
            marginTop: theme.spacing(3),
        },
        '& .MuiInputBase-input': {
            borderRadius: 4,
            position: 'relative',
            backgroundColor: theme.palette.background.paper,
            border: '0px solid #ced4da',
            fontSize: 16,
            transition: theme.transitions.create(['border-color', 'box-shadow']),
            // Use the system font instead of the default Roboto font.
        },
    }));

    //user picked new grind as their choice to purchase
    const handleSelectGrind = async (loft: string, bounce: string, grind: string) => {
        let newGrindRecommendations = [...grindRecommendations];
        newGrindRecommendations.forEach(recommendation => {
            if (recommendation.loft === loft) {
                recommendation.selectedBounce = bounce;
                recommendation.selectedGrind = grind;
            }
        })
        setGrindRecommendations(newGrindRecommendations);
        //save golfer object to local storage so we don't lose their grind preferences
        let newGolfer = { ...golfer, grindRecommendations: newGrindRecommendations };
        setGolfer(newGolfer);
        //if this is from the fitting history page, the email has already been sent so don't save updates to local db
        if (!(fromFittingHistory || fromInQueue)) {
            await GolferHelper.upsertGolfer(newGolfer);
        }
    }

    const handleFittingNotes = async (event: ChangeEvent<HTMLTextAreaElement>) => {
        let newGolfer = { ...golfer, fittingNotes: event.target.value };
        setGolfer(newGolfer);
        //if this is from the fitting history page, the email has already been sent so don't save updates to local db
        if (!(fromFittingHistory || fromInQueue)) {
            await GolferHelper.upsertGolfer(newGolfer);
        }
    }

    const cbGolferUpdate = async (cbGolfer: Golfer) => {
        let newGolfer = { ...golfer, firstName: cbGolfer.firstName, lastName: cbGolfer.lastName, fullName: cbGolfer.fullName, email: cbGolfer.email, phone: cbGolfer.phone, smsOptIn: cbGolfer.smsOptIn  };
        setGolfer(newGolfer);
        //if this is from the fitting history page, the email has already been sent so don't save updates to local db
        if (!(fromFittingHistory || fromInQueue)) {
            await GolferHelper.upsertGolfer(newGolfer);
        }
    }


    const showPopup = (id: number, grindIx: number, weightingIx: number) => {
        let showIt = false;
        switch (id) {
            case 0:
                showIt = showTutorial && tutorialId === id;
                break;
            case 1:
                showIt = showTutorial && tutorialId === id && grindIx === 0 && weightingIx === 0;
                break;
            case 2:
                showIt = showTutorial && tutorialId === id && grindIx === 1 && weightingIx === 1;
                break;
            case 3:
                showIt = showTutorial && tutorialId === id;
                break;
            case 4:
                showIt = showTutorial && tutorialId === id;
                break;
        }
        return showIt;
    }

    const handleTutorialNext = async () => {
        if (tutorialId === 4) {
            navigate('/apps/wedgefitting/fittingprocess');
        }
        setTutorialId(tutorialId + 1);
    }

    const handleTutorialCancel = async () => {
        await TutorialHelper.disable();
        setShowTutorial(false);
        navigate('/apps/wedgefitting/golferinfo');
    }

    const handleDialogClose = () => {
        if (fitterIsAuthorized) {
            setShowFinishedPopup(false);
            navigate('/apps/wedgefitting/golferinfo');

        } else {
            //fitter refresh token expired. Redirect to fitter login
            navigate('/apps/wedgefitting');
        }
    }

    const handleBackToFitting = () => {
        navigate('/apps/wedgefitting/fitterhistory');
    }

    return (
        <div className="why-grinds-form" style={{ height: '100%' }}>
            <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'flex' } }} className='navbar'>
                <AppBar position="fixed" className='appbar'>
                    <Container maxWidth="lg">
                        <Toolbar>
                            <Grid container>
                                <Grid item xs={4}>
                                    <Typography className='name' variant="h6" component="div" sx={{ marginTop: '10px', display: { xs: 'none', md: 'flex' } }}>
                                        <Person sx={{ width: '30px', marginRight: '5px', marginTop: '-10px', height: '2em' }}></Person>
                                        <GolferMenu golfer={golfer} cbgolferupdate={cbGolferUpdate}></GolferMenu>
                                    </Typography>

                                </Grid>

                                <HtmlTooltip
                                    title={
                                        <>
                                            <Typography color="inherit" sx={{ height: '75px' }}><FormattedMessage id="quick.start.email.specs" /></Typography>
                                            <br />
                                            <div style={{ textAlign: 'right' }}>
                                                <Button disableRipple onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                                <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                            </div>
                                        </>
                                    }
                                    open={showPopup(4, 0, 0)}
                                    placement={'bottom-start'}
                                    arrow={false}
                                    maxwidth={800}
                                >


                                    <Grid item xs={3}>
                                        <Typography variant="h6" component="div" className='title' sx={{ display: 'flex', justifyContent: 'center', marginRight: '0px !important', marginTop: '7px' }}>
                                            {intlTitle} <span style={{ marginLeft: '15px', fontWeight: '100' }}>6/6</span>
                                        </Typography>
                                    </Grid>
                                </HtmlTooltip>
                                {!(fromFittingHistory || fromInQueue) &&
                                    <Grid item xs={5} container justifyContent="flex-end">
                                        <Button disableRipple type="submit" variant="contained" onClick={handleBack} className='cancel-button'><FormattedMessage id="general.back" /></Button>
                                        <Button disableRipple type="submit" variant="contained" onClick={handleSubmit} className='submit-button' sx={{ width: '175px !important' }}>{lblSubmit}</Button>
                                    </Grid>
                                }
                                {fromFittingHistory &&
                                    <Grid item xs={5} container justifyContent="flex-end">
                                        <Button disableRipple type="submit" variant="contained" onClick={handleBackToFitting} sx={{ width: "200px !important" }} className='submit-button'><FormattedMessage id="general.back.to.history" /></Button>
                                    </Grid>
                                }
                                {fromInQueue &&
                                    <Grid item xs={5} container justifyContent="flex-end">
                                        <Button disableRipple type="submit" variant="contained" onClick={handleSubmit} className='cancel-button' sx={{ width: '175px !important' }}>{intlResendSpecs}</Button>
                                        <Button disableRipple type="submit" variant="contained" onClick={handleBackToFitting} sx={{ width: "200px !important" }} className='submit-button'><FormattedMessage id="general.back.to.history" /></Button>
                                    </Grid>
                                }
                            </Grid>
                        </Toolbar>
                    </Container>
                </AppBar>
            </Box>

            <Container maxWidth="lg" sx={{ height: '100%' }}>
                <Grid container className="grid-container">
                    <HtmlTooltip
                        title={
                            <>
                                <Typography color="inherit" sx={{ height: '150px' }}><FormattedMessage id="quick.start.review" /></Typography>
                                <br />
                                <div style={{ textAlign: 'right' }}>
                                    <Button disableRipple onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                    <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                </div>
                            </>
                        }
                        open={showPopup(0, 0, 0)}
                        placement={'bottom'}
                        arrow={false}
                        maxwidth={800}
                    >
                        <Grid item xs={12} sx={{ marginTop: '64px' }}></Grid>
                    </HtmlTooltip>
                    <Grid
                        item
                        xs={12} md={9}
                        className={"grid-column"}
                    >


                        <Grid container sx={{ width: '99%', marginTop: '10px' }}>
                            {grindRecommendations.map((grind, grindIx) => {
                                return (
                                    <Paper className="paper" key={grindIx}>
                                        <Grid container className='row' sx={{ marginTop: '10px !important' }}>
                                        </Grid>
                                        <Grid container className='row' sx={{ marginTop: '0px !important', paddingTop: '5px' }}>
                                            <Grid item sm={4}>
                                                {usingSm11 && (
                                                    <>
                                                        {grind.selectedGrind === "D" && <img style={{ height: '150px' }} src={grind_d_sm11}></img>}
                                                        {grind.selectedGrind === "F" && <img style={{ height: '150px' }} src={grind_f_sm11}></img>}
                                                        {grind.selectedGrind === "K" && <img style={{ height: '150px' }} src={grind_k_sm11}></img>}
                                                        {grind.selectedGrind === "M" && <img style={{ height: '150px' }} src={grind_m_sm11}></img>}
                                                        {grind.selectedGrind === "S" && <img style={{ height: '150px' }} src={grind_s_sm11}></img>}
                                                        {grind.selectedGrind === "T" && <img style={{ height: '150px' }} src={grind_t_sm11}></img>}
                                                    </>
                                                )}
                                                <br />
                                                <span style={{ fontSize: '30px' }}>{grind.loft}.{grind.selectedBounce}</span> <span style={{ fontSize: '30px', fontWeight: '900' }}>{grind.selectedGrind}</span>
                                            </Grid>
                                            <Grid item sm={3}>
                                                {grind.grindWeightings.map((weighting, weightingIx) => {
                                                    return (
                                                        <div key={weightingIx}>
                                                            <HtmlTooltip
                                                                title={
                                                                    <>
                                                                        <Typography color="inherit"><FormattedMessage id="quick.start.lofts.ranked" /></Typography>
                                                                        <br />
                                                                        <div style={{ textAlign: 'right' }}>
                                                                            <Button disableRipple onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                                                            <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                                                        </div>
                                                                    </>
                                                                }
                                                                open={showPopup(1, grindIx, weightingIx)}
                                                                placement={'right-start'}
                                                                arrow={true}
                                                                maxwidth={400}
                                                            >

                                                                <div key={weightingIx}>
                                                                    <HtmlTooltip
                                                                        title={
                                                                            <>
                                                                                <Typography color="inherit"><FormattedMessage id="quick.start.change.grind" /></Typography>
                                                                                <br />
                                                                                <div style={{ textAlign: 'right' }}>
                                                                                    <Button disableRipple onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                                                                    <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                                                                </div>
                                                                            </>
                                                                        }
                                                                        open={showPopup(2, grindIx, weightingIx)}
                                                                        placement={'right'}
                                                                        arrow={true}
                                                                        maxwidth={400}
                                                                    >

                                                                        <Grid container onClick={() => { handleSelectGrind(grind.loft, weighting.bounce, weighting.grind) }} className={`grind-container ${grind.selectedBounce === weighting.bounce && grind.selectedGrind === weighting.grind ? "selected" : ""}`}>
                                                                            <Grid item xs={5}>
                                                                                <Typography className="loft">{grind.loft}.{weighting.bounce} <span className="grind">{weighting.grind}</span></Typography>
                                                                            </Grid>
                                                                            <Grid item xs={7}>
                                                                                <Typography className="match"> {weighting.weightPercent}% <FormattedMessage id="notes.match" /></Typography>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </HtmlTooltip>
                                                                </div>
                                                            </HtmlTooltip>
                                                        </div>
                                                    )
                                                })
                                                }
                                            </Grid>
                                            <Grid item sm={5} sx={{ paddingLeft: '20px', textAlign: 'left' }}>
                                                <div style={{ marginBottom: '10px', fontWeight: '700' }}><FormattedMessage id="notes.title" /></div>
                                                {grind.notes.map((note, noteIx) => {
                                                    return (
                                                        <div style={{ marginBottom: '10px' }} key={noteIx}>{note.value}</div>
                                                    )
                                                })}
                                            </Grid>
                                        </Grid>

                                    </Paper>
                                )
                            })}
                        </Grid>



                    </Grid>
                    <Grid item xs={12} md={3} className={"grid-column"}  >
                        <Grid container>
                            <Grid item md={12} className='page-title' sx={{ marginTop: '10px' }}>

                                <HtmlTooltip
                                    title={
                                        <>
                                            <Typography color="inherit"><FormattedMessage id="quick.start.different.conditions" /></Typography>
                                            <br />
                                            <div style={{ textAlign: 'right' }}>
                                                <Button disableRipple onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                                <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                            </div>
                                        </>
                                    }
                                    open={showPopup(3, 0, 0)}
                                    placement={'right'}
                                    arrow={true}
                                    maxwidth={400}
                                >

                                    <Paper sx={{ width: '98%', marginLeft: '3px' }}>
                                        <div className="sidebar"><FormattedMessage id="notes.your.game" /></div>
                                        <Grid container className='row' sx={{ marginTop: '10px !important' }}>
                                        </Grid>
                                        <Grid container className='row' sx={{ marginTop: '0px !important', paddingTop: '5px' }}>
                                            <Grid item sm={6} sx={{ paddingLeft: '10px', fontSize: 'medium', textAlign: 'center' }}><FormattedMessage id="history.course" /></Grid>
                                            <Grid item sm={6} sx={{ paddingLeft: '10px', fontSize: 'medium' }}>

                                                <Box sx={{ minWidth: 100 }}>
                                                    <FormControl fullWidth variant="standard">
                                                        <Select
                                                            labelId="demo-customized-select-label"
                                                            id="demo-customized-select"
                                                            input={<BootstrapInput />}
                                                            value={golfer.courseConditions}
                                                            name="courseConditions"
                                                            onChange={handleSelectedChange}
                                                        >
                                                            <MenuItem value={"Soft"}>{intlSoft}</MenuItem>
                                                            <MenuItem value={"Slightly Soft"}>{intlSlightlySoft}</MenuItem>
                                                            <MenuItem value={"Medium"}>{intlMedium}</MenuItem>
                                                            <MenuItem value={"Slightly Firm"}>{intlSlightlyFirm}</MenuItem>
                                                            <MenuItem value={"Firm"}>{intlFirm}</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Box>

                                            </Grid>
                                        </Grid>
                                        <Grid container className='row' sx={{ marginTop: '0px !important', paddingTop: '5px' }}>
                                            <Grid item sm={6} sx={{ paddingLeft: '10px', fontSize: 'medium', textAlign: 'center' }}><FormattedMessage id="shot.selection.bunker" /></Grid>
                                            <Grid item sm={6} sx={{ paddingLeft: '10px', fontSize: 'medium' }}>
                                                <Box sx={{ minWidth: 100 }}>
                                                    <FormControl fullWidth variant="standard">
                                                        <Select
                                                            labelId="demo-customized-select-label"
                                                            id="demo-customized-select"
                                                            input={<BootstrapInput />}
                                                            value={golfer.bunkerConditions}
                                                            name="bunkerConditions"
                                                            onChange={handleSelectedChange}
                                                        >
                                                            <MenuItem value={"Soft"}>{intlSoft}</MenuItem>
                                                            <MenuItem value={"Slightly Soft"}>{intlSlightlySoft}</MenuItem>
                                                            <MenuItem value={"Medium"}>{intlMedium}</MenuItem>
                                                            <MenuItem value={"Slightly Firm"}>{intlSlightlyFirm}</MenuItem>
                                                            <MenuItem value={"Firm"}>{intlFirm}</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Box>

                                            </Grid>
                                        </Grid>
                                        <Grid container className='row' sx={{ marginTop: '0px !important', paddingTop: '5px' }}>
                                            <Grid item sm={6} sx={{ paddingLeft: '10px', fontSize: 'medium', textAlign: 'center' }}><FormattedMessage id="history.bunker.confidence" /></Grid>
                                            <Grid item sm={6} sx={{ paddingLeft: '10px', fontSize: 'medium' }}>
                                                <Box sx={{ minWidth: 100 }}>
                                                    <FormControl fullWidth variant="standard">
                                                        <Select
                                                            labelId="demo-customized-select-label"
                                                            id="demo-customized-select"
                                                            input={<BootstrapInput />}
                                                            value={golfer.confidence}
                                                            name="confidence"
                                                            onChange={handleSelectedChange}
                                                        >
                                                            <MenuItem value={"Low"}>{intlLow}</MenuItem>
                                                            <MenuItem value={"Less"}>{intlLess}</MenuItem>
                                                            <MenuItem value={"Medium"}>{intlMedium}</MenuItem>
                                                            <MenuItem value={"More"}>{intlMore}</MenuItem>
                                                            <MenuItem value={"High"}>{intlHigh}</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        <Grid container className='row' sx={{ marginTop: '10px !important' }}>
                                        </Grid>
                                    </Paper>
                                </HtmlTooltip>
                            </Grid>
                        </Grid>
                        <Grid container sx={{ width: '98%', marginLeft: '3px' }}>
                            <Grid item sm={12} className='page-title'>
                                <Paper>
                                    <div className="sidebar"><FormattedMessage id="notes.your.technique" /></div>
                                    <Grid container className='row' sx={{ marginTop: '10px !important' }}>
                                    </Grid>
                                    <Grid container className='row' sx={{ marginTop: '0px !important', paddingTop: '5px' }}>
                                        <Grid item sm={6} sx={{ paddingLeft: '10px', fontSize: 'medium', textAlign: 'center' }}><FormattedMessage id="shot.selection.full" /></Grid>
                                        <Grid item sm={6} sx={{ paddingLeft: '10px', fontSize: 'medium' }}><FormattedMessage id={`now.hitting.${yourTechnique.full.toLowerCase()}`} /></Grid>
                                    </Grid>
                                    <Grid container className='row' sx={{ marginTop: '0px !important', paddingTop: '5px' }}>
                                        <Grid item sm={6} sx={{ paddingLeft: '10px', fontSize: 'medium', textAlign: 'center' }}><FormattedMessage id='golfer.info.medium' /></Grid>
                                        <Grid item sm={6} sx={{ paddingLeft: '10px', fontSize: 'medium' }}><FormattedMessage id={`now.hitting.${yourTechnique.pitch.toLowerCase()}`} /></Grid>
                                    </Grid>
                                    <Grid container className='row' sx={{ marginTop: '0px !important', paddingTop: '5px' }}>
                                        <Grid item sm={6} sx={{ paddingLeft: '10px', fontSize: 'medium', textAlign: 'center' }}><FormattedMessage id='shot.selection.openface' /></Grid>
                                        <Grid item sm={6} sx={{ paddingLeft: '10px', fontSize: 'medium' }}><FormattedMessage id={`now.hitting.${yourTechnique.openFace.toLowerCase()}`} /></Grid>
                                    </Grid>
                                    <Grid container className='row' sx={{ marginTop: '10px !important' }}>
                                    </Grid>
                                </Paper>
                            </Grid>
                        </Grid>

                        <Grid container sx={{ width: '98%', marginLeft: '3px' }}>
                            <Grid item sm={12} className='page-title'>
                                <Paper>
                                    <div className="sidebar"><FormattedMessage id="notes.fitting.notes" /></div>
                                    <Grid container className='row' sx={{ marginTop: '10px !important' }}>
                                    </Grid>
                                    <Grid container className='row' sx={{ marginTop: '0px !important', paddingTop: '5px' }}>
                                        <Grid item sm={12} sx={{ paddingLeft: '10px', fontSize: 'medium', textAlign: 'center' }}>
                                            <TextareaAutosize
                                                aria-label="empty textarea"
                                                minRows={3}
                                                name="fitingNotes"
                                                onChange={handleFittingNotes}
                                                value={golfer.fittingNotes}
                                                className="fitting-notes"
                                            />

                                        </Grid>
                                    </Grid>
                                    <Grid container className='row' sx={{ marginTop: '10px !important' }}>
                                    </Grid>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Container>

            <Dialog
                open={showFinishedPopup}
                onClose={handleDialogClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogContent>
                    <DialogContentText id="alert-dialog-description" sx={{ color: 'black', width: '400px', textAlign: "center" }}>
                        <>
                            {showSpinner && <>
                                <span>{intlSendingToServer}</span><br />
                                <span className="sk-wave" style={{ display: 'inline-flex', marginRight: '0px', marginTop: '20px', textAlign: "center" }} >
                                    <span className="sk-wave-rect" style={{ backgroundColor: 'red' }}></span>
                                    <span className="sk-wave-rect" style={{ backgroundColor: 'red' }}></span>
                                    <span className="sk-wave-rect" style={{ backgroundColor: 'red' }}></span>
                                    <span className="sk-wave-rect" style={{ backgroundColor: 'red' }}></span>
                                    <span className="sk-wave-rect" style={{ backgroundColor: 'red' }}></span>
                                </span>
                            </>}
                        </>
                        {!showSpinner && <>
                            <span>{orderConfirmationMessage}</span>
                        </>}
                    </DialogContentText>
                </DialogContent>
                <DialogActions className='appbar'>
                    <Button disableRipple variant='outlined' className='submit-button' onClick={handleDialogClose} sx={{ color: 'black', borderColor: 'black' }}><FormattedMessage id="general.close" /></Button>
                </DialogActions>
            </Dialog>

        </div>
    )
}
